import { z } from 'zod';
import { orgRoleSchema } from '../enums.js';
/** Identifiant lisible dans les URLs. Sert aussi de garde-fou contre les collisions de nom. */
export const slugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(2)
    .max(50)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Seuls les minuscules, chiffres et tirets sont autorisés');
export const createOrganizationSchema = z.object({
    name: z.string().trim().min(2).max(100),
    slug: slugSchema.optional(),
});
export const updateOrganizationSchema = z.object({
    name: z.string().trim().min(2).max(100).optional(),
    /** Rétention en jours, `null` pour conserver sans limite (PRD NFR-20). */
    retentionDays: z.number().int().min(1).max(3650).nullable().optional(),
});
export const organizationSchema = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    /** Quota de stockage en octets, `null` si illimité. */
    storageQuotaBytes: z.number().nullable(),
    /** Consommation courante, recalculée à chaque ingestion et suppression. */
    storageUsedBytes: z.number(),
    retentionDays: z.number().nullable(),
    createdAt: z.coerce.date(),
});
export const memberSchema = z.object({
    userId: z.string(),
    email: z.string(),
    displayName: z.string(),
    role: orgRoleSchema,
    totpEnabled: z.boolean(),
    joinedAt: z.coerce.date(),
});
export const updateMemberRoleSchema = z.object({
    role: orgRoleSchema,
});
/**
 * Bascule du consentement « partager l'état d'architecture avec l'éditeur » (ADR-0037 §4). Endpoint
 * **dédié et audité**, distinct des réglages ordinaires : chaque activation/révocation laisse une
 * trace (souveraineté — le client garde la main sur ce que le fournisseur observe de son instance).
 */
export const healthConsentSchema = z.object({
    shareHealthWithVendor: z.boolean(),
});
/** Invitation en attente, telle qu'exposée à l'administration (jamais le jeton en clair). */
export const invitationSummarySchema = z.object({
    id: z.string(),
    email: z.string(),
    role: orgRoleSchema,
    /** Nom de l'auteur de l'invitation, joint pour l'affichage. */
    invitedByName: z.string().nullable(),
    expiresAt: z.coerce.date(),
    createdAt: z.coerce.date(),
});
/**
 * Résultat d'un envoi d'invitation. Deux cas :
 * - `invited` : l'e-mail n'a pas encore de compte → un jeton d'acceptation est émis **une seule
 *   fois** (`token`/`inviteUrl`), à transmettre à la personne (pas d'e-mail automatique à ce stade) ;
 * - `added` : l'e-mail a déjà un compte → la personne est **directement** ajoutée comme membre.
 */
export const inviteResultSchema = z.object({
    status: z.enum(['invited', 'added']),
    token: z.string().optional(),
    inviteUrl: z.string().optional(),
    expiresAt: z.coerce.date().optional(),
    member: memberSchema.optional(),
});
/** Référentiel de labels d'annotation, propre à chaque organisation (PRD FR-27). */
export const createLabelSchema = z.object({
    name: z.string().trim().min(1).max(60),
    /** Couleur hexadécimale utilisée dans le canevas d'annotation et la timeline. */
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Couleur hexadécimale attendue, ex. #0f62fe'),
    description: z.string().trim().max(500).optional(),
    parentId: z.string().nullable().optional(),
});
export const labelSchema = createLabelSchema.extend({
    id: z.string(),
    organizationId: z.string(),
    /** Nom affiché de l'auteur de la catégorie, joint pour l'affichage (FR-27). */
    createdByName: z.string().nullable(),
    createdAt: z.coerce.date(),
});
//# sourceMappingURL=organization.js.map